import React, { useEffect, useState, useRef, useMemo } from "react";
import { AddCircleOutline, Delete, Edit } from "@mui/icons-material";
import {
  Button,
  IconButton,
  Switch,
  Tooltip,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch, useSelector } from "react-redux";
import EmployerViewController from "../../redux/actions/EmployerApiAction";
import EmployerFormModal from "./AddEmployer";
import "../../styles/styles.css";
import "../../styles/layout.css";
import PageSizeSelector from "../../components/ag grid/PageSizeSelector";
import ReusableGrid from "../../components/ag grid/ReusableGrid";
import EmployerViewModal from "./EmployerViewModal";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ExportButton from "../../components/ag grid/ExportButton";
import ListPageLayout from "../../components/layout/ListPageLayout";
import { toolbarButtonSx } from "../../components/auth/authTheme";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import EmployerMobileView from "./EmployerMobileView";

const EmployerList = () => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);

  const [showModal, setShowModal] = useState(false);
  const [editEmployer, setEditEmployer] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedEmployer, setSelectedEmployer] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { getEmployer, deleteEmployer, updateEmployer } =
    EmployerViewController();
  const { employers } = useSelector((state) => state.employer);

  useEffect(() => {
    dispatch(getEmployer());
  }, [dispatch]);

  const filteredEmployers = useMemo(() => {
    if (!searchTerm.trim()) return employers || [];
    const q = searchTerm.toLowerCase();
    return (employers || []).filter((emp) => {
      const fullName = `${emp.first_name || ""} ${emp.last_name || ""}`.toLowerCase();
      const email = emp.email?.toLowerCase() || "";
      const company = emp.company?.company_name?.toLowerCase() || "";
      return fullName.includes(q) || email.includes(q) || company.includes(q);
    });
  }, [employers, searchTerm]);

  const handleDelete = (id) => dispatch(deleteEmployer(id));
  const handleEdit = (employer) => {
    setEditEmployer(employer);
    setShowModal(true);
  };
  const handleView = (employer) => {
    setSelectedEmployer(employer);
    setViewModalOpen(true);
  };

  const handleToggleActive = (employer, checked) => {
    const updatedData = {
      ...employer,
      is_active: checked,
      company: employer.company?.id,
    };
    dispatch(updateEmployer(employer.id, updatedData)).catch((err) => {
      console.error("Failed to update status", err);
    });
  };

  const columnDefs = useMemo(
    () => [
    {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      width: 52,
      maxWidth: 52,
      minWidth: 52,
      pinned: "left",
      suppressSizeToFit: true,
      sortable: false,
      headerClass: "checkbox-selection-header",
      cellClass: "checkbox-selection-cell",
    },
    {
      headerName: "Sr. No.",
      valueGetter: (params) => params.node.rowIndex + 1,
      width: 72,
      maxWidth: 80,
      minWidth: 72,
      suppressSizeToFit: true,
    },
    {
      headerName: "Name",
      valueGetter: (params) =>
        `${params.data.first_name || ""} ${params.data.last_name || ""}`.trim(),
      flex: 1.5,
      minWidth: 160,
    },
    {
      headerName: "Company",
      valueGetter: (params) => params.data ? params.data.company?.company_name || "—" : "—",
      tooltipValueGetter: (params) => params.data ? params.data.company?.company_name || "—" : "",
      flex: 1.5,
      minWidth: 150,
      cellStyle: { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    },
    {
      headerName: "Email",
      field: "email",
      flex: 1.5,
      minWidth: 180,
    },
    {
      headerName: "Phone",
      field: "mobile_number",
      width: 130,
      minWidth: 120,
      maxWidth: 150,
      suppressSizeToFit: true,
    },
    {
      headerName: "Active",
      field: "is_active",
      width: 90,
      minWidth: 90,
      maxWidth: 100,
      suppressSizeToFit: true,
      cellRenderer: (params) => {
        const handleChange = (e) => {
          const updatedValue = e.target.checked;
          const updatedData = {
            ...params.data,
            is_active: updatedValue,
            company: params.data.company?.id,
          };
          params.node.setDataValue("is_active", updatedValue);
          dispatch(updateEmployer(params.data.id, updatedData)).catch((err) => {
            console.error("Failed to update status", err);
            params.node.setDataValue("is_active", !updatedValue);
          });
        };

        return (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Switch
              checked={Boolean(params.value)}
              onChange={handleChange}
              color="primary"
              size="small"
            />
          </div>
        );
      },
    },
    {
      headerName: "Actions",
      field: "actions",
      pinned: "right",
      width: 130,
      minWidth: 130,
      maxWidth: 130,
      suppressSizeToFit: true,
      sortable: false,
      cellRenderer: (params) => (
        <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
          <Tooltip title="View">
            <IconButton
              color="primary"
              size="small"
              onClick={() => handleView(params.data)}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEdit(params.data)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              sx={{ color: "#d32f2f" }}
              onClick={() => handleDelete(params.data.id)}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ], []
  );

  return (
    <>
      {isMobile ? (
        <EmployerMobileView
          employers={filteredEmployers}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          handleView={handleView}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          handleToggleActive={handleToggleActive}
          openModalForCreate={() => {
            setEditEmployer(null);
            setShowModal(true);
          }}
          canCreate={true}
          canEdit={true}
          canDelete={true}
        />
      ) : (
        <ListPageLayout
          title="Employers"
          subtitle="Manage organization employers and their access"
          headerAction={
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddCircleOutline />}
              onClick={() => {
                setEditEmployer(null);
                setShowModal(true);
              }}
              sx={toolbarButtonSx}
            >
              Add Employer
            </Button>
          }
          toolbarRight={
            <>
              <ExportButton
                gridRef={gridRef}
                fileName="EmployerList.xlsx"
                exportColumns={[
                  "first_name",
                  "last_name",
                  "email",
                  "mobile_number",
                  "is_active",
                ]}
                disabled={selectedRows.length === 0}
                onSelectionCleared={() => setSelectedRows([])}
              />
              <TextField
                size="small"
                placeholder="Search employer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ minWidth: 220 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <PageSizeSelector
                pageSize={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              />
            </>
          }
        >
          <div className="list-page-grid">
            <ReusableGrid
              rowData={filteredEmployers}
              columnDefs={columnDefs}
              gridRef={gridRef}
              pagination={true}
              pageSize={pageSize}
              onSelectionChanged={(rows) => setSelectedRows(rows)}
              emptyMessage="No employers yet"
              emptyHint='Click "Add Employer" to create your first employer.'
            />
          </div>
        </ListPageLayout>
      )}

      <EmployerFormModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        initialData={editEmployer}
      />
      <EmployerViewModal
        show={viewModalOpen}
        handleClose={() => setViewModalOpen(false)}
        employer={selectedEmployer}
      />
    </>
  );
};

export default EmployerList;
